import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';
import { ROUTES } from './routes';
import { AppLayout } from '@/components/layout';
import { useWorkspaceStore } from '@/store';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/HomePage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const SignUpPage = lazy(() => import('@/pages/auth/SignUpPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const RegisterUserPage = lazy(() => import('@/pages/users/RegisterUserPage'));
const NotificationPage = lazy(() => import('@/pages/notifications/NotificationPage'));
const AcceptInvitationPage = lazy(() => import('@/pages/invitations/AcceptInvitationPage'));
const RejectInvitationPage = lazy(() => import('@/pages/invitations/RejectInvitationPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Workspace pages
const WorkspaceDashboard = lazy(() => import('@/pages/workspace/WorkspaceDashboard'));
const NewProjectPage = lazy(() => import('@/pages/workspace/NewProjectPage'));
const WorkspaceInvitationPage = lazy(() => import('@/pages/workspace/WorkspaceInvitationPage'));
const BoardPage = lazy(() => import('@/pages/board/BoardPage'));
const ProjectDashboardPage = lazy(() => import('@/pages/project/ProjectDashboardPage'));
const ProjectSettingsPage = lazy(() => import('@/pages/project/ProjectSettingsPage'));
const CalendarPage = lazy(() => import('@/pages/project/CalendarPage'));
const ReportsPage = lazy(() => import('@/pages/project/ReportsPage'));
const WorkspaceReportsPage = lazy(() => import('@/pages/workspace/WorkspaceReportsPage'));
const OrgReportsPage = lazy(() => import('@/pages/organization/OrgReportsPage'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage'));
const IssueDetailPage = lazy(() => import('@/pages/issue/IssueDetailPage'));
const BacklogPage = lazy(() => import('@/pages/backlog/BacklogPage'));
const TimelinePage = lazy(() => import('@/pages/timeline/TimelinePage'));

/**
 * Loading fallback component for lazy-loaded routes
 */
const PageLoader: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
    }}
  >
    <CircularProgress />
  </Box>
);

/**
 * Redirects to the first workspace or shows a fallback
 */
const WorkspaceRedirect: React.FC = () => {
  const workspaces = useWorkspaceStore((state: any) => state.workspaces);
  const initialized = useWorkspaceStore((state: any) => state.workspacesInitialized);

  // Wait until AppLayout has finished fetching workspaces
  if (!initialized) {
    return <PageLoader />;
  }
  if (workspaces.length > 0) {
    return <Navigate to={`/w/${workspaces[0].workspaceSlug}`} replace />;
  }
  // No workspaces — AppLayout's onboarding wizard will open automatically
  return <Box sx={{ p: 4 }} />;
};

/**
 * Main application router
 */
export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route
            path={ROUTES.HOME}
            element={
              <PublicRoute>
                <HomePage />
              </PublicRoute>
            }
          />

          {/* Auth Routes - Redirect if already authenticated */}
          <Route
            path={ROUTES.LOGIN}
            element={
              <PublicRoute redirectAuthenticated>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path={ROUTES.SIGNUP}
            element={
              <PublicRoute redirectAuthenticated>
                <SignUpPage />
              </PublicRoute>
            }
          />
          <Route
            path={ROUTES.FORGOT_PASSWORD}
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path={ROUTES.RESET_PASSWORD}
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />

          {/* User Registration (from invitation) */}
          <Route
            path={ROUTES.REGISTER_USER}
            element={
              <PublicRoute>
                <RegisterUserPage />
              </PublicRoute>
            }
          />

          {/* Invitation handling */}
          <Route
            path={ROUTES.ACCEPT_INVITATION}
            element={
              <PublicRoute>
                <AcceptInvitationPage />
              </PublicRoute>
            }
          />
          <Route
            path={ROUTES.REJECT_INVITATION}
            element={
              <PublicRoute>
                <RejectInvitationPage />
              </PublicRoute>
            }
          />

          {/* Protected Routes with Layout */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* Legacy routes - all redirect to workspace */}
            <Route path="/dashboard" element={<WorkspaceRedirect />} />
            <Route path="/todo-task" element={<WorkspaceRedirect />} />
            <Route path="/recursive-task" element={<WorkspaceRedirect />} />
            <Route path="/user" element={<WorkspaceRedirect />} />
            <Route path="/group-notification" element={<WorkspaceRedirect />} />

            {/* Keep these as standalone pages */}
            <Route path={ROUTES.PROFILE} element={<ProfilePage />} />
            <Route path={ROUTES.NOTIFICATION} element={<NotificationPage />} />

            {/* Workspace Routes */}
            <Route path={ROUTES.WORKSPACE} element={<WorkspaceDashboard />} />
            <Route path={ROUTES.WORKSPACE_PROJECTS_NEW} element={<NewProjectPage />} />
            <Route path={ROUTES.WORKSPACE_PROJECTS} element={<WorkspaceDashboard />} />
            <Route path={ROUTES.WORKSPACE_SETTINGS} element={<WorkspaceDashboard />} />
            <Route path={ROUTES.WORKSPACE_MEMBERS} element={<WorkspaceDashboard />} />
            <Route path={ROUTES.WORKSPACE_REPORTS} element={<WorkspaceReportsPage />} />
            <Route path={ROUTES.ORG_REPORTS} element={<OrgReportsPage />} />

            {/* Project Routes */}
            <Route path={ROUTES.WORKSPACE_PROJECT} element={<ProjectDashboardPage />} />
            <Route path={ROUTES.WORKSPACE_PROJECT_BOARD} element={<BoardPage />} />
            <Route path={ROUTES.WORKSPACE_PROJECT_BACKLOG} element={<BacklogPage />} />
            <Route path={ROUTES.WORKSPACE_PROJECT_TIMELINE} element={<TimelinePage />} />
            <Route path={ROUTES.WORKSPACE_PROJECT_SPRINTS} element={<BacklogPage />} />
            <Route path={ROUTES.WORKSPACE_PROJECT_SETTINGS} element={<ProjectSettingsPage />} />
            <Route path={ROUTES.WORKSPACE_PROJECT_CALENDAR} element={<CalendarPage />} />
            <Route path={ROUTES.WORKSPACE_PROJECT_REPORTS} element={<ReportsPage />} />

            {/* Billing */}
            <Route path="/billing" element={<BillingPage />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/subscriptions" element={<AdminDashboard />} />

            {/* Issue Detail Page */}
            <Route path={ROUTES.WORKSPACE_ISSUE} element={<IssueDetailPage />} />

          </Route>

          {/* Workspace Invitation Routes — public so the page can handle its own auth + sessionStorage redirect */}
          <Route
            path={ROUTES.WORKSPACE_INVITATION_ACCEPT}
            element={<WorkspaceInvitationPage action="accept" />}
          />
          <Route
            path={ROUTES.WORKSPACE_INVITATION_REJECT}
            element={<WorkspaceInvitationPage action="reject" />}
          />

          {/* Privacy Policy */}
          <Route
            path={ROUTES.PRIVACY_POLICY}
            element={
              <PublicRoute>
                <PrivacyPolicyPage />
              </PublicRoute>
            }
          />

          {/* Redirects */}
          <Route path="" element={<Navigate to={ROUTES.HOME} replace />} />

          {/* 404 Not Found */}
          <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default AppRouter;
